import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, Users, Gamepad2, BookOpen, ArrowRight, Crown, LogIn } from "lucide-react";
import Marquee from "react-fast-marquee";
import { Button } from "@/components/ui/button";
import DynamicPage, { useCMSPage } from "@/components/cms/DynamicPage";
import { CampaignCarousel } from "@/components/cms/SectionRenderers";
import { useAuth } from "@/contexts/AuthContext";
import { usePlatform } from "@/contexts/PlatformContext";
import { AuthModal } from "@/components/auth/AuthModal";
import { useTheme } from "next-themes";

const HORIZONTAL_LOGO = "/assets/logo-horizontal.png";
const APP_HOME_LOGO = "/assets/logo-home-app.png";

function AppHomePage() {
  const { user } = useAuth();

  const gamesPlayedToday = user?.games_played_today || 0;
  const gamesLimitDay = user?.games_limit_day ?? 0;
  const isUnlimitedDay = user?.games_unlimited_day || gamesLimitDay === -1;
  const gamesRemainingToday = isUnlimitedDay
    ? null
    : Math.max(0, gamesLimitDay - gamesPlayedToday);

  const primaryActions = [
    {
      icon: Gamepad2,
      title: "Jogar Agora",
      description: "Entre direto nos quizzes gratuitos do dia",
      link: "/jogos",
      accent: "border-[#fccf1f]/30 bg-[#fccf1f]/10 text-[#fccf1f]",
    },
    {
      icon: Crown,
      title: "Quiz Club",
      description: "Libere mais jogos e vantagens exclusivas",
      link: "/clube",
      accent: "border-purple-500/30 bg-purple-500/10 text-purple-400",
    },
    {
      icon: Calendar,
      title: "Eventos",
      description: "Veja os próximos presenciais e especiais",
      link: "/eventos",
      accent: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    },
    {
      icon: BookOpen,
      title: "Temas",
      description: "Explore temas, sugestões e favoritos",
      link: "/temas",
      accent: "border-sky-500/30 bg-sky-500/10 text-sky-400",
    },
  ];

  const secondaryActions = [
    { icon: Users, title: "Meu Perfil", link: "/perfil" },
    { icon: Calendar, title: "Minha Carteira", link: "/meus-ingressos" },
    { icon: ArrowRight, title: "Placar", link: "/placar" },
    { icon: BookOpen, title: "Perguntas", link: "/perguntas" },
  ];

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-6" data-testid="app-home-page">
      <div className="mx-auto flex max-w-md flex-col gap-5 px-4">
        <section className="rounded-[28px] border border-white/10 bg-zinc-950/80 p-5 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1 pr-2">
              <img
                src={APP_HOME_LOGO}
                alt="Lord of the Quiz"
                className="h-auto w-full max-w-[220px] object-contain"
              />
            </div>
            <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-right">
              <p className="font-outfit text-[10px] uppercase tracking-[0.2em] text-zinc-500">Plano</p>
              <p className="font-nunito text-sm font-bold text-white">
                {user?.subscription_plan === "master"
                  ? "Lorde Supremo"
                  : user?.subscription_plan === "pro"
                  ? "Quizzeiro Pro"
                  : "Jogador Casual"}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <p className="font-outfit text-xs uppercase tracking-[0.24em] text-zinc-500">Bem-vindo</p>
            <h1 className="font-nunito text-2xl font-black text-white">
              {user?.display_name?.split(" ")[0] || user?.name?.split(" ")[0] || "Lorde"}
            </h1>
            <p className="font-outfit text-sm text-zinc-400">
              Seu atalho para jogar, subir no ranking e acompanhar eventos.
            </p>
          </div>
        </section>

        <section className="rounded-[24px] border border-[#fccf1f]/20 bg-gradient-to-br from-[#fccf1f]/12 via-zinc-950 to-zinc-950 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-outfit text-xs uppercase tracking-[0.2em] text-[#fccf1f]/70">Hoje no app</p>
              <h2 className="mt-1 font-nunito text-xl font-black text-white">
                {isUnlimitedDay ? "Jogos ilimitados liberados" : `${gamesRemainingToday} jogos disponíveis`}
              </h2>
            </div>
            <div className="rounded-2xl border border-[#fccf1f]/20 bg-black/20 p-3">
              <Gamepad2 className="h-5 w-5 text-[#fccf1f]" />
            </div>
          </div>
          <p className="mt-3 font-outfit text-sm text-zinc-300">
            {isUnlimitedDay
              ? "Aproveite para jogar à vontade e explorar os quizzes premium."
              : `Você já usou ${gamesPlayedToday} ${gamesPlayedToday === 1 ? "jogo" : "jogos"} do seu dia.`}
          </p>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-nunito text-lg font-bold text-white">Acessos principais</h2>
            <Link to="/jogos" className="font-outfit text-xs uppercase tracking-[0.18em] text-[#fccf1f]">
              abrir jogos
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {primaryActions.map((action) => (
              <Link
                key={action.title}
                to={action.link}
                className="rounded-[22px] border border-white/10 bg-zinc-900/60 p-4 transition-transform hover:-translate-y-0.5"
              >
                <div className={`mb-4 inline-flex rounded-2xl border p-2.5 ${action.accent}`}>
                  <action.icon className="h-5 w-5" />
                </div>
                <h3 className="font-nunito text-base font-bold text-white">{action.title}</h3>
                <p className="mt-1 font-outfit text-xs leading-relaxed text-zinc-400">
                  {action.description}
                </p>
              </Link>
            ))}
          </div>
        </section>

        <section className="rounded-[24px] border border-white/10 bg-zinc-950/70 p-4">
          <p className="mb-3 font-outfit text-xs uppercase tracking-[0.2em] text-zinc-500">Atalhos</p>
          <div className="grid grid-cols-2 gap-2">
            {secondaryActions.map((action) => (
              <Link
                key={action.title}
                to={action.link}
                className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3 text-sm text-zinc-200 transition-colors hover:bg-white/[0.06]"
              >
                <action.icon className="h-4 w-4 text-zinc-500" />
                <span className="font-outfit">{action.title}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

// Original static content as fallback
function StaticHomePage() {
  const { user, loading: authLoading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";

  const features = [
    {
      icon: Calendar,
      title: "Eventos Regulares",
      description: "Quiz Shows semanais em diversos locais da cidade",
      link: "/eventos",
    },
    {
      icon: Users,
      title: "Contrate-nos",
      description: "Eventos corporativos, festas e convenções",
      link: "/contratar",
    },
    {
      icon: Gamepad2,
      title: "Jogos Online",
      description: "Jogue quiz interativos direto do seu navegador",
      link: "/jogos",
    },
    {
      icon: BookOpen,
      title: "Temas & Perguntas",
      description: "Banco com centenas de temas e perguntas",
      link: "/temas",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="lotq-home-hero relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/6324813/pexels-photo-6324813.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-black/70"></div>
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at center, rgba(252,207,31,0.15) 0%, rgba(5,5,5,0) 70%)",
            }}
          ></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10 text-center py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex justify-center mb-6"
            data-testid="hero-title"
          >
            <img
              src={HORIZONTAL_LOGO}
              alt="Lord of the Quiz"
              className="h-auto w-full max-w-[500px] md:max-w-[600px] lg:max-w-[700px] object-contain"
              style={{
                filter: isLight
                  ? "brightness(0) saturate(100%) drop-shadow(0 0 12px rgba(110,70,214,0.12))"
                  : "drop-shadow(0 0 15px rgba(252,207,31,0.3))",
              }}
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-outfit text-xl md:text-2xl text-zinc-200 mb-12 max-w-3xl mx-auto"
            data-testid="hero-subtitle"
          >
            A melhor experiência de Quiz Show. Eventos presenciais, jogos
            online e muito mais!
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/eventos">
              <Button
                className="bg-[#fccf1f] text-black font-nunito font-black uppercase tracking-wide hover:bg-[#e6bc1c] hover:scale-105 transition-all shadow-[0_0_20px_rgba(252,207,31,0.3)] rounded-lg px-8 py-6 text-lg"
                data-testid="cta-eventos"
              >
                Ver Eventos <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/contratar">
              <Button
                className="bg-transparent border-2 border-[#fccf1f] text-[#fccf1f] font-nunito font-bold uppercase hover:bg-[#fccf1f] hover:text-black transition-all rounded-lg px-8 py-6 text-lg"
                data-testid="cta-contratar"
              >
                Contratar Serviço
              </Button>
            </Link>
          </motion.div>

          {/* Auth CTA */}
          {!authLoading && !user && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-8 flex flex-col items-center gap-3"
            >
              <p className="font-outfit text-sm text-zinc-300">Já tem conta ou quer criar uma?</p>
              <Button
                onClick={() => setShowAuth(true)}
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white font-nunito font-bold uppercase tracking-wide hover:bg-white/20 hover:border-[#fccf1f]/50 transition-all rounded-full px-8 py-3"
                data-testid="hero-login-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Entrar / Criar Conta
              </Button>
            </motion.div>
          )}

          {!authLoading && user && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-8"
            >
              <Link to="/clube">
                <Button
                  className="bg-white/10 backdrop-blur-sm border border-[#fccf1f]/30 text-[#fccf1f] font-nunito font-bold uppercase tracking-wide hover:bg-[#fccf1f]/10 hover:border-[#fccf1f]/60 transition-all rounded-full px-8 py-3"
                  data-testid="hero-quiz-club-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Quiz Club — Meu Plano
                </Button>
              </Link>
            </motion.div>
          )}
        </div>

        {/* Scrolling Ticker */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#fccf1f] py-4 z-20">
          <Marquee gradient={false} speed={50}>
            <span className="font-nunito font-black text-black text-xl uppercase mx-8">
              Quiz Show Toda Semana
            </span>
            <span className="text-black text-2xl mx-8">★</span>
            <span className="font-nunito font-black text-black text-xl uppercase mx-8">
              Eventos Corporativos
            </span>
            <span className="text-black text-2xl mx-8">★</span>
            <span className="font-nunito font-black text-black text-xl uppercase mx-8">
              Jogos Online Grátis
            </span>
            <span className="text-black text-2xl mx-8">★</span>
            <span className="font-nunito font-black text-black text-xl uppercase mx-8">
              Festas Privadas
            </span>
            <span className="text-black text-2xl mx-8">★</span>
          </Marquee>
        </div>
      </section>

      <CampaignCarousel
        className="py-8 md:py-12"
        content={{
          fullBleed: true,
          autoPlay: true,
          intervalSeconds: 7,
          slides: [
            {
              eyebrow: "O QUIZ VAI ALÉM DA PERGUNTA",
              title: "Encontre sua próxima noite de jogo.",
              description: "Acompanhe os eventos presenciais, reúna sua equipe e transforme uma noite comum em uma disputa memorável.",
              ctaLabel: "Explorar eventos",
              ctaLink: "/eventos",
              accent: "gold",
            },
            {
              eyebrow: "QUIZ CLUB",
              title: "Jogue mais. Aproveite mais.",
              description: "Créditos, partidas, comunidade e benefícios para quem quer viver o universo Lord of the Quiz por inteiro.",
              ctaLabel: "Conhecer o Quiz Club",
              ctaLink: "/clube",
              accent: "purple",
            },
          ],
        }}
      />

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-transparent" data-testid="features-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-nunito font-bold text-3xl md:text-5xl text-white text-center mb-16">
            O que oferecemos
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to={feature.link}>
                  <div
                    className="bg-zinc-900/40 backdrop-blur-md border border-white/10 p-8 rounded-2xl hover:border-[#fccf1f]/50 transition-all duration-300 group cursor-pointer hover:-translate-y-2 h-full"
                    data-testid={`feature-card-${index}`}
                  >
                    <feature.icon
                      className="text-[#fccf1f] mb-4 group-hover:scale-110 transition-transform"
                      size={48}
                    />
                    <h3 className="font-nunito font-bold text-2xl text-[#fccf1f] mb-3">
                      {feature.title}
                    </h3>
                    <p className="font-outfit text-base text-zinc-300">
                      {feature.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="lotq-photo-surface py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/5054648/pexels-photo-5054648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10 text-center">
          <h2 className="font-nunito font-bold text-3xl md:text-5xl text-white mb-6">
            Pronto para testar seus conhecimentos?
          </h2>
          <p className="font-outfit text-lg md:text-xl text-zinc-300 mb-8 max-w-2xl mx-auto">
            Junte-se a nós no próximo evento ou jogue online agora mesmo!
          </p>
          <Link to="/jogos">
            <Button
              className="bg-[#fccf1f] text-black font-nunito font-black uppercase tracking-wide hover:bg-[#e6bc1c] hover:scale-105 transition-all shadow-[0_0_20px_rgba(252,207,31,0.3)] rounded-lg px-8 py-6 text-lg"
              data-testid="cta-jogar-agora"
            >
              Jogar Agora <Gamepad2 className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </div>
  );
}

// Main HomePage component - uses CMS content if available, otherwise static fallback
export default function HomePage() {
  const { isApp } = usePlatform();
  const { page, loading } = useCMSPage('home');
  const { user, loading: authLoading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  if (isApp) {
    return <AppHomePage />;
  }
  
  // Show loading spinner
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#fccf1f]"></div>
      </div>
    );
  }

  const content = (page && page.sections && page.sections.length > 0)
    ? <DynamicPage slug="home" fallbackContent={<StaticHomePage />} />
    : <StaticHomePage />;

  return (
    <>
      {content}

      {/* Floating Auth CTA - visible on all homepage variants */}
      {!authLoading && !user && (
        <div className="fixed bottom-20 right-6 z-40" data-testid="floating-auth-cta">
          <button
            onClick={() => setShowAuth(true)}
            className="flex items-center gap-2 bg-[#fccf1f] text-black font-nunito font-bold uppercase text-sm tracking-wide px-6 py-3 rounded-full shadow-[0_4px_20px_rgba(252,207,31,0.4)] hover:bg-[#e6bc1c] hover:scale-105 transition-all"
            data-testid="floating-login-btn"
          >
            <LogIn className="w-4 h-4" />
            Entrar / Criar Conta
          </button>
        </div>
      )}

      {!authLoading && user && (
        <div className="fixed bottom-20 right-6 z-40" data-testid="floating-club-cta">
          <Link to="/clube">
            <button className="flex items-center gap-2 bg-zinc-900/90 backdrop-blur-sm border border-[#fccf1f]/40 text-[#fccf1f] font-nunito font-bold uppercase text-sm tracking-wide px-6 py-3 rounded-full shadow-lg hover:border-[#fccf1f] hover:scale-105 transition-all">
              <Crown className="w-4 h-4" />
              Quiz Club
            </button>
          </Link>
        </div>
      )}

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
}
